import { Injectable } from '@angular/core';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';
import { ChatBadge, ChatConversation, ChatMessage, ChatNotification } from '../models/chat.model';

// skip-loader - caly komunikator/powiadomienia to floating widget, nie nawigacja strony, wiec
// globalny loader (LoadingInterceptor) nigdzie tutaj nie ma sensu - ANI w tle (polling co
// 5-30s), ANI przy akcjach usera (wysylka, tworzenie rozmowy, ack itd.): kazde jego mignieice
// bylo osobnym zgloszeniem Patryka (28-29.09.2026), wiec dolaczany jest do KAZDEGO wywolania
// w tym pliku.
const SILENT = new HttpHeaders({ 'skip-loader': 'true' });

@Injectable({ providedIn: 'root' })
export class ChatApiService {
  private readonly url = '/chat';

  constructor(private readonly http: HttpClient) {}

  badge(): Observable<ChatBadge> {
    return this.http.get<ChatBadge>(`${this.url}/badge`, { headers: SILENT });
  }

  conversations(includeInactive = false): Observable<ChatConversation[]> {
    return this.http.get<ChatConversation[]>(`${this.url}/conversations`, {
      params: includeInactive ? { includeInactive: 'true' } : {},
      headers: SILENT,
    });
  }

  createDirect(profilId: number): Observable<ChatConversation> {
    return this.http.post<ChatConversation>(`${this.url}/conversations/direct`, { profilId }, { headers: SILENT });
  }

  createGroup(name: string, profilIds: number[]): Observable<ChatConversation> {
    return this.http.post<ChatConversation>(`${this.url}/conversations/group`, { name, profilIds }, { headers: SILENT });
  }

  addParticipant(conversationId: number, profilId: number, showFullHistory: boolean): Observable<void> {
    return this.http.post<void>(`${this.url}/conversations/${conversationId}/participants`, { profilId, showFullHistory }, { headers: SILENT });
  }

  // skip-loader: wywolywane w tle po KAZDYM 5s odpycie otwartej rozmowy (patrz
  // ChatStateService) - bez tego globalny loader (LoadingInterceptor) mrugal cala strona
  // co 5s (zgloszenie Patryka 28.09.2026), dokladnie jak dawny dzwonek powiadomien.
  markConversationRead(conversationId: number): Observable<{ ok: boolean }> {
    return this.http.post<{ ok: boolean }>(`${this.url}/conversations/${conversationId}/read`, {}, { headers: SILENT });
  }

  messages(conversationId: number, beforeId?: number): Observable<ChatMessage[]> {
    const params: Record<string, string> = { limit: '50' };
    if (beforeId) {
      params.beforeId = String(beforeId);
    }
    return this.http.get<ChatMessage[]>(`${this.url}/conversations/${conversationId}/messages`, {
      params,
      headers: SILENT,
    });
  }

  // skip-loader: wysylka z otwartego okienka czatu - globalny loader mrugal cala strone przy
  // kazdej wyslanej wiadomosci (zgloszenie Patryka 29.09.2026), tak jak reszta akcji widgetu.
  sendMessage(conversationId: number, body: string): Observable<ChatMessage> {
    return this.http.post<ChatMessage>(`${this.url}/conversations/${conversationId}/messages`, { body }, { headers: SILENT });
  }

  editMessage(messageId: number, body: string): Observable<ChatMessage> {
    return this.http.patch<ChatMessage>(`${this.url}/messages/${messageId}`, { body }, { headers: SILENT });
  }

  deleteMessage(messageId: number): Observable<{ ok: boolean }> {
    return this.http.delete<{ ok: boolean }>(`${this.url}/messages/${messageId}`, { headers: SILENT });
  }

  // ── Powiadomienia ────────────────────────────────────────────────────

  notifications(limit = 50): Observable<{ unread: number; items: ChatNotification[] }> {
    return this.http.get<{ unread: number; items: ChatNotification[] }>(`${this.url}/notifications`, {
      params: { limit: String(limit) },
      headers: SILENT,
    });
  }

  readNotification(id: number): Observable<{ ok: boolean }> {
    return this.http.post<{ ok: boolean }>(`${this.url}/notifications/${id}/read`, {}, { headers: SILENT });
  }

  // skip-loader: wywolywane automatycznie przy KAZDYM otwarciu panelu powiadomien (patrz
  // ChatStateService.loadNotifications) - bez tego globalny loader mrugal cala strona przy
  // kazdym kliknieciu dzwonka (zgloszenie Patryka 29.09.2026), tak jak markConversationRead.
  readAllNotifications(): Observable<{ marked: number }> {
    return this.http.post<{ marked: number }>(`${this.url}/notifications/read-all`, {}, { headers: SILENT });
  }

  acknowledgeNotification(id: number): Observable<{ ok: boolean }> {
    return this.http.post<{ ok: boolean }>(`${this.url}/notifications/${id}/ack`, {}, { headers: SILENT });
  }

  // ── Wybor aktywnych pracownikow (nowa rozmowa / dodanie do grupy) ───────
  //
  // GET /profil bez parametru "where" domyslnie zwraca tylko aktywnych
  // (profils.aktywnosc=1, patrz ProfilService.findAll w backendzie) -
  // dokladnie to, czego potrzebuje picker. skip-loader - to samo okienko dialogowe
  // ma juz wlasny mat-progress-bar na czas ladowania, globalny loader tu tylko przeszkadza.
  activeProfils(): Observable<{ id: number; imie: string; nazwisko: string }[]> {
    return this.http
      .get<{ count: number; list: { id: number; imie: string; nazwisko: string }[] }>('/profil', { headers: SILENT })
      .pipe(map((res) => res.list ?? []));
  }
}
